<template>
  <a :class="classList" v-bind="attributes" tabindex="-1" @click.stop.prevent v-if="isDisabled">
    <i v-if="hasIcon" :class="classIcon" class="eb-icon"></i>
    <span class="nav-link-label">{{ displayLabel }}</span>
    <b-badge v-if="badge && badge.text" :variant="badge.variant">{{badge.text}}</b-badge>
  </a>
  <a :href="url" :class="classList" v-bind="attributes" target="_blank" rel="noopener noreferrer" v-else-if="isExternalLink">
    <i v-if="hasIcon" :class="classIcon" class="eb-icon"></i>
    <span class="nav-link-label">{{ displayLabel }}</span>
    <b-badge v-if="badge && badge.text" :variant="badge.variant">{{badge.text}}</b-badge>
  </a>
  <router-link :to="url" class="width-class" :class="classList" v-bind="attributes" v-else>
    <i v-if="hasIcon" :class="classIcon" class="eb-icon"></i>
    <span class="nav-link-label">{{ displayLabel }}</span>
    <b-badge v-if="badge && badge.text" :variant="badge.variant">{{badge.text}}</b-badge>
  </router-link>
</template>

<script>
export default {
  name: 'sidebar-nav-link',
  props: {
    name: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    badge: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: String,
      default: '',
    },
    classes: {
      type: [String, Array, Object],
      default: '',
    },
    attributes: {
      type: Object,
      default: () => Object.create(null),
    },
  },
  computed: {
    classList() {
      return [
        'nav-link',
        this.hasIcon ? 'nav-link-has-icon' : 'nav-link-no-icon',
        this.linkVariant,
        ...this.disabledClasses,
        ...this.attrClasses,
        ...this.itemClasses,
      ];
    },
    classIcon() {
      return [
        'nav-icon',
        this.icon,
      ];
    },
    displayLabel() {
      return this.displayName || this.name;
    },
    hasIcon() {
      return Boolean(this.icon && this.icon.trim());
    },
    linkVariant() {
      return this.variant ? `nav-link-${this.variant}` : '';
    },
    itemClasses() {
      return this.getClassArray(this.classes);
    },
    attrClasses() {
      return this.getClassArray(this.attributes.class);
    },
    disabledClasses() {
      return this.isDisabled ? 'disabled'.split(' ') : [];
    },
    isDisabled() {
      return Boolean(this.attributes.disabled);
    },
    isExternalLink() {
      return Boolean(this.url.substring(0, 4) === 'http');
    },
  },
  methods: {
    getClassArray(classes) {
      if (!classes) return [];
      if (typeof classes === 'string' || classes instanceof String) {
        return classes.split(' ');
      }
      if (Array.isArray(classes)) {
        return classes;
      }
      return Object.keys(classes).filter((i) => classes[i]);
    },
  },
};
</script>
<style>
.eb-icon{
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 15px !important;
  width: 20px;
  min-width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
}
.width-class{
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
}
.sidebar .nav-link {
  display: flex !important;
  align-items: center;
  gap: 4px;
  min-height: 42px;
  padding: 0.5rem 1rem !important;
  box-sizing: border-box;
}
.sidebar .nav-link.nav-link-no-icon {
  gap: 0;
}
.sidebar .nav-link .nav-link-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transform: none !important;
}
.sidebar .nav-link .badge {
  margin-left: auto;
  flex-shrink: 0;
}
.sidebar .nav-dropdown-bg > .nav-item {
  margin-bottom: 0.1rem !important;
}
.sidebar .nav-dropdown-bg .nav-link {
  min-height: 40px;
  width: 100%;
  padding: 0.56rem 0.85rem !important;
}
.sidebar .nav-dropdown-bg .nav-link.nav-link-no-icon {
  padding-left: 3.3rem !important;
}
.sidebar .nav-dropdown-bg .nav-link .nav-link-label,
.sidebar .nav-dropdown-bg .nav-link.router-link-active .nav-link-label,
.sidebar .nav-dropdown-bg .nav-link.router-link-exact-active .nav-link-label,
.sidebar .nav-dropdown-bg .nav-link.active .nav-link-label {
  font-weight: 500;
  letter-spacing: -0.01em;
}
.eb-icon:hover {
  color: rgba(255, 255, 255, 0.96) !important;
  text-decoration: none;
  background: none !important;
}

@media (min-width: 992px) {
  .sidebar-minimized .sidebar .nav-link:not(.nav-dropdown-toggle) {
    justify-content: flex-start;
    padding-left: 0 !important;
    padding-right: 0 !important;
    gap: 0 !important;
  }

  .sidebar-minimized .sidebar .nav-link .nav-link-label,
  .sidebar-minimized .sidebar .nav-link .badge {
    display: none !important;
  }

  .sidebar-minimized .sidebar .nav-link:not(.nav-dropdown-toggle) .eb-icon,
  .sidebar-minimized .sidebar .nav-link:not(.nav-dropdown-toggle) .nav-icon {
    width: 50px;
    min-width: 50px;
    justify-content: center;
    margin: 0 !important;
  }

  .sidebar-minimized .sidebar .nav-item:hover > .nav-link:not(.nav-dropdown-toggle),
  .sidebar-minimized .sidebar .nav-item:focus-within > .nav-link:not(.nav-dropdown-toggle) {
    justify-content: flex-start;
    padding-left: 0 !important;
    padding-right: 0 !important;
    gap: 0 !important;
  }

  .sidebar-minimized .sidebar .nav-item:hover > .nav-link:not(.nav-dropdown-toggle) .nav-link-label,
  .sidebar-minimized .sidebar .nav-item:hover > .nav-link:not(.nav-dropdown-toggle) .badge,
  .sidebar-minimized .sidebar .nav-item:focus-within > .nav-link:not(.nav-dropdown-toggle) .nav-link-label,
  .sidebar-minimized .sidebar .nav-item:focus-within > .nav-link:not(.nav-dropdown-toggle) .badge {
    display: block !important;
  }

  .sidebar-minimized .sidebar .nav-dropdown.open .nav-dropdown-items .nav-link,
  .sidebar-minimized .sidebar .nav-dropdown:hover .nav-dropdown-items .nav-link,
  .sidebar-minimized .sidebar .nav-dropdown:focus-within .nav-dropdown-items .nav-link {
    justify-content: flex-start;
    padding-left: 0.9rem !important;
    padding-right: 0.9rem !important;
    gap: 4px !important;
  }

  .sidebar-minimized .sidebar .nav-dropdown.open .nav-dropdown-items .nav-link .nav-link-label,
  .sidebar-minimized .sidebar .nav-dropdown.open .nav-dropdown-items .nav-link .badge,
  .sidebar-minimized .sidebar .nav-dropdown:hover .nav-dropdown-items .nav-link .nav-link-label,
  .sidebar-minimized .sidebar .nav-dropdown:hover .nav-dropdown-items .nav-link .badge,
  .sidebar-minimized .sidebar .nav-dropdown:focus-within .nav-dropdown-items .nav-link .nav-link-label,
  .sidebar-minimized .sidebar .nav-dropdown:focus-within .nav-dropdown-items .nav-link .badge {
    display: block !important;
  }

  .sidebar-minimized .sidebar .eb-icon,
  .sidebar-minimized .sidebar .nav-icon {
    margin: 0 !important;
  }
}
</style>
